import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { shippingApi } from '../../api/shipping';

// 模拟发货数据
const MOCK_SHIPPING_DATA = [
  {
    id: 1,
    orderNumber: 'SO-20230615-001',
    customerName: '张三',
    productName: '石佛寺文化纪念品',
    shippingCompany: '顺丰速运',
    trackingNumber: 'SF123456789',
    shippingStatus: 'PENDING',
    shippingAddress: '北京市海淀区中关村软件园',
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    orderNumber: 'SO-20230620-002',
    customerName: '李四',
    productName: '佛教工艺画',
    shippingCompany: '中通快递',
    trackingNumber: 'ZTO987654321',
    shippingStatus: 'SHIPPED',
    shippingAddress: '上海市徐汇区田林路',
    createTime: '2023-06-20 14:45:00',
    updateTime: '2023-06-20 14:45:00'
  },
  {
    id: 3,
    orderNumber: 'SO-20230701-003',
    customerName: '王五',
    productName: '佛珠手链',
    shippingCompany: '圆通速递',
    trackingNumber: 'YTO246813579',
    shippingStatus: 'FAILED',
    shippingAddress: '杭州市西湖区文三路',
    createTime: '2023-07-01 09:15:00',
    updateTime: '2023-07-01 09:15:00'
  }
];

// 获取发货列表
export const fetchShippingList = createAsyncThunk(
  'shipping/fetchList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await shippingApi.getShippingList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取发货列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_SHIPPING_DATA.filter(shipping => {
        const orderNumberMatch = !params.orderNumber || 
          shipping.orderNumber.toLowerCase().includes(params.orderNumber.toLowerCase());
        
        const customerNameMatch = !params.customerName || 
          shipping.customerName.toLowerCase().includes(params.customerName.toLowerCase());
        
        const shippingCompanyMatch = !params.shippingCompany || 
          shipping.shippingCompany === params.shippingCompany;
        
        const shippingStatusMatch = !params.shippingStatus || 
          shipping.shippingStatus === params.shippingStatus;
        
        const timeMatch = !params.startTime || !params.endTime || 
          (shipping.createTime >= params.startTime && shipping.createTime <= params.endTime);
        
        return orderNumberMatch && customerNameMatch && 
               shippingCompanyMatch && shippingStatusMatch && timeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 发货
export const shipOrder = createAsyncThunk(
  'shipping/shipOrder',
  async ({ id, ...shippingData }, { rejectWithValue }) => {
    try {
      const response = await shippingApi.shipOrder(id, shippingData);
      return response.data;
    } catch (error) {
      console.warn('发货失败，使用模拟数据', error);
      const index = MOCK_SHIPPING_DATA.findIndex(s => s.id === id);
      if (index !== -1) {
        const updatedShipping = { 
          ...MOCK_SHIPPING_DATA[index], 
          ...shippingData,
          shippingStatus: 'SHIPPED',
          updateTime: new Date().toLocaleString()
        };
        MOCK_SHIPPING_DATA[index] = updatedShipping;
        return updatedShipping;
      }
      return rejectWithValue('发货订单不存在');
    }
  }
);

// 重新发货
export const reshippOrder = createAsyncThunk(
  'shipping/reshippOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await shippingApi.reshippOrder(id);
      return response.data;
    } catch (error) {
      console.warn('重新发货失败，使用模拟数据', error);
      const index = MOCK_SHIPPING_DATA.findIndex(s => s.id === id);
      if (index !== -1) {
        const updatedShipping = { 
          ...MOCK_SHIPPING_DATA[index],
          shippingStatus: 'SHIPPED',
          updateTime: new Date().toLocaleString()
        };
        MOCK_SHIPPING_DATA[index] = updatedShipping;
        return updatedShipping;
      }
      return rejectWithValue('重新发货订单不存在');
    }
  }
);

const shippingSlice = createSlice({
  name: 'shipping',
  initialState: {
    shippingList: [],
    shippingPagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setShippingPagination: (state, action) => {
      state.shippingPagination = { ...state.shippingPagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取发货列表
      .addCase(fetchShippingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShippingList.fulfilled, (state, action) => {
        state.loading = false;
        state.shippingList = action.payload.list;
        state.shippingPagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchShippingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.shippingList = MOCK_SHIPPING_DATA;
        state.shippingPagination.total = MOCK_SHIPPING_DATA.length;
      })
      // 发货
      .addCase(shipOrder.fulfilled, (state, action) => {
        const index = state.shippingList.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.shippingList[index] = action.payload;
        }
      })
      // 重新发货
      .addCase(reshippOrder.fulfilled, (state, action) => {
        const index = state.shippingList.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.shippingList[index] = action.payload;
        }
      });
  }
});

export const { setShippingPagination } = shippingSlice.actions;
export default shippingSlice.reducer; 